import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/ApiService';
import { generateMetaPurchaseEventId, generateMetaPurchaseEventTime } from '../utils/metaPurchaseEventId';
import {
  META_PURCHASE_EVENT_NAME,
  metaCustomerTypeFromEsMayorista,
} from '../constants/metaPixel';
import { setMpCheckoutPedidoId } from '../utils/mpCheckoutSession';

const CarritoContext = createContext();

export const useCarrito = () => {
  const context = useContext(CarritoContext);
  if (!context) {
    throw new Error('useCarrito debe ser usado dentro de un CarritoProvider');
  }
  return context;
};

export const CarritoProvider = ({ children }) => {
  const [carritoItems, setCarritoItems] = useState(() => {
    const savedCarrito = localStorage.getItem('carrito');
    return savedCarrito ? JSON.parse(savedCarrito) : [];
  });

  const [showCarritoSidebar, setShowCarritoSidebar] = useState(false);

  useEffect(() => {
    localStorage.setItem('carrito', JSON.stringify(carritoItems));
  }, [carritoItems]);

  const agregarAlCarrito = (producto) => {
    setCarritoItems(prev => {
      const existente = prev.find(item => item.id === producto.id);

      if (existente) {
        return prev.map(item =>
          item.id === producto.id
            ? { ...item, cantidad: item.cantidad + 1 }
            : item
        );
      }

      return [...prev, { ...producto, cantidad: 1 }];
    });
    setShowCarritoSidebar(true);
  };

  const incrementarCantidad = (id) => {
    setCarritoItems(prev =>
      prev.map(item =>
        item.id === id ? { ...item, cantidad: item.cantidad + 1 } : item
      )
    );
  };

  const decrementarCantidad = (id) => {
    setCarritoItems(prev =>
      prev.map(item =>
        item.id === id && item.cantidad > 1
          ? { ...item, cantidad: item.cantidad - 1 }
          : item
      )
    );
  };

  const eliminarItem = (id) => {
    setCarritoItems(prev => prev.filter(item => item.id !== id));
  };

  const vaciarCarrito = () => {
    setCarritoItems([]);
  };

  const finalizarCompra = async (nombreContacto, telefonoContacto, metodoPago = 'mercadopago') => {
    try {
      if (!nombreContacto || !telefonoContacto || nombreContacto.trim() === '' || telefonoContacto.trim() === '') {
        return { success: false, error: 'Debe proporcionar nombre y teléfono de contacto' };
      }

      if (telefonoContacto.trim().length < 8) {
        return { success: false, error: 'El teléfono debe tener al menos 8 caracteres' };
      }

      const toNumber = (val) => {
        const n = Number(val);
        return Number.isFinite(n) ? n : 0;
      };

      const productos = carritoItems.map(item => ({
        id: item.id,
        nombre: item.nombre,
        precio: toNumber(item.precio),
        preciomenor: toNumber(item.preciomenor),
        cantidad: item.cantidad,
      }));

      const cantidadTotalCalc = carritoItems.reduce((acc, item) => acc + item.cantidad, 0);
      const subtotalMinoristaCalc = carritoItems.reduce((acc, item) => acc + (toNumber(item.preciomenor) * item.cantidad), 0);
      const subtotalMayoristaCalc = carritoItems.reduce((acc, item) => acc + (toNumber(item.precio) * item.cantidad), 0);
      const esMayoristaCalc = subtotalMayoristaCalc >= 60000 && cantidadTotalCalc >= 6;
      const montoTotal = esMayoristaCalc ? subtotalMayoristaCalc : subtotalMinoristaCalc;
      const metaCustomerType = metaCustomerTypeFromEsMayorista(esMayoristaCalc);
      const metaEventId = generateMetaPurchaseEventId();
      const metaEventTime = generateMetaPurchaseEventTime();

      const respuesta = await ApiService.post('/pedidos', {
        productos,
        montoTotal,
        descuento: 0,
        estado: 'CREADO',
        metodoPago,
        nombreContacto: nombreContacto.trim(),
        telefonoContacto: telefonoContacto.trim(),
        metaEventId,
        metaEventName: META_PURCHASE_EVENT_NAME,
        metaCustomerType,
        metaEventTime,
      });

      const initPoint = respuesta?.data?.init_point;
      const pedidoId = respuesta?.data?.pedido?._id;

      if (metodoPago === 'efectivo') {
        if (!pedidoId) {
          return {
            success: false,
            error: 'No se recibió la confirmación del pedido. Intentá nuevamente.',
          };
        }

        return {
          success: true,
          metodoPago: 'efectivo',
          pedidoId,
          montoTotal,
          whatsappLink: respuesta?.data?.whatsappLink,
          metaEventId,
          metaEventName: META_PURCHASE_EVENT_NAME,
          metaCustomerType,
          metaEventTime,
        };
      }

      if (!initPoint) {
        return {
          success: false,
          error: 'No se recibió el enlace de pago de Mercado Pago. Intentá nuevamente.',
        };
      }

      setMpCheckoutPedidoId(pedidoId);
      window.location.href = initPoint;

      return {
        success: true,
        redirecting: true,
        pedidoId,
        metaEventId,
        metaEventName: META_PURCHASE_EVENT_NAME,
        metaCustomerType,
        metaEventTime,
      };
    } catch (error) {
      console.error('Error al finalizar compra:', error);

      let mensajeError = 'Error al procesar la compra';
      let stockErrors = null;

      if (error?.payload) {
        mensajeError = error.payload.message || error.payload.error || error.payload.msg || JSON.stringify(error.payload);

        if (error.payload.details && Array.isArray(error.payload.details)) {
          stockErrors = {};
          error.payload.details.forEach(detail => {
            if (detail.productId && detail.error) {
              stockErrors[detail.productId] = detail.error;
            }
          });
        }
      } else if (error?.message) {
        mensajeError = error.message;
      }

      return { success: false, error: mensajeError, stockErrors };
    }
  };

  const cantidadTotal = carritoItems.reduce((total, item) => total + item.cantidad, 0);
  const toNumber = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };
  const subtotalMinorista = carritoItems.reduce((acc, item) => acc + (toNumber(item.preciomenor) * item.cantidad), 0);
  const subtotalMayorista = carritoItems.reduce((acc, item) => acc + (toNumber(item.precio) * item.cantidad), 0);
  const esMayorista = subtotalMayorista >= 60000 && cantidadTotal >= 6;
  const total = esMayorista ? subtotalMayorista : subtotalMinorista;

  return (
    <CarritoContext.Provider value={{
      carritoItems,
      agregarAlCarrito,
      incrementarCantidad,
      decrementarCantidad,
      eliminarItem,
      vaciarCarrito,
      finalizarCompra,
      cantidadTotal,
      subtotalMinorista,
      subtotalMayorista,
      total,
      esMayorista,
      showCarritoSidebar,
      setShowCarritoSidebar
    }}>
      {children}
    </CarritoContext.Provider>
  );
};
